import { ImageSquare, Plus } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { createAttachmentRepository } from '@/data/repositories/attachment-repository';
import type { Attachment, AttachmentOwnerType, Uuid } from '@/data/db';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { useToast } from '@/components/feedback/toast';
import { cn } from '@/lib/cn';
import { describeQuota, needsResize, validateImageFile } from './image-rules';
import { processImage, readImageSize } from './process-image';
import { ImageViewer } from './image-viewer';
import { useAttachmentCount, useAttachmentsFor, useBlobUrl } from './use-attachments';

interface AttachmentStripProps {
  ownerType: AttachmentOwnerType;
  ownerId: Uuid;
}

/** 任務與追蹤紀錄的圖片附件列。清單只讀縮圖，大圖只在檢視器打開時才讀。 */
export function AttachmentStrip({ ownerType, ownerId }: AttachmentStripProps) {
  const { db, save } = useWorkspace();
  const toast = useToast();
  const repository = useMemo(() => createAttachmentRepository(db), [db]);

  const attachments = useAttachmentsFor(ownerType, ownerId);
  const totalCount = useAttachmentCount() ?? 0;
  const quota = describeQuota(totalCount);

  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [pendingResize, setPendingResize] = useState<{
    file: File;
    width: number;
    height: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function store(file: File, resize: boolean) {
    setBusy(true);
    try {
      const processed = await processImage(file, { resize });
      await save.run(async () => {
        await repository.add({ ownerType, ownerId, fileName: file.name, processed });
      });
      toast.show({
        message: '已加入圖片',
        detail: `${file.name}・${quota.label}`,
        tone: 'success',
      });
    } catch (error) {
      toast.show({
        message: '這張圖片沒有加成功',
        detail: error instanceof Error ? error.message : undefined,
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  async function pick(file: File) {
    const invalid = validateImageFile(file);
    if (invalid) {
      toast.show({ message: '這個檔案不能加入', detail: invalid, tone: 'danger' });
      return;
    }

    try {
      const size = await readImageSize(file);
      if (needsResize(size.width, size.height)) {
        setPendingResize({ file, ...size });
        return;
      }
    } catch {
      // 讀不到尺寸就直接照原樣處理，不要卡住使用者。
    }

    await store(file, false);
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[0.8125rem] font-medium text-text-muted">
          圖片
          <span className="tabular ml-2 text-[0.75rem]">{quota.label}</span>
        </h3>

        <label
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5',
            'text-[0.8125rem] font-medium text-text-muted hover:bg-accent-soft hover:text-text',
            (busy || quota.full) && 'pointer-events-none opacity-45',
          )}
        >
          <Plus size={14} />
          {busy ? '處理中' : '加入圖片'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            disabled={busy || quota.full}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void pick(file);
              event.target.value = '';
            }}
          />
        </label>
      </div>

      {attachments === undefined ? (
        <p className="text-[0.8125rem] text-text-muted">載入中…</p>
      ) : attachments.length === 0 ? (
        <p className="flex items-center gap-2 rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-[0.8125rem] text-text-muted">
          <ImageSquare size={15} aria-hidden="true" />
          郵件截圖、收據或掃描圖片都可以放這裡。只接受 JPEG、PNG 與 WebP。
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <li key={attachment.id}>
              <ThumbnailButton attachment={attachment} onOpen={() => setViewing(attachment)} />
            </li>
          ))}
        </ul>
      )}

      <ImageViewer
        attachment={viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        onDelete={(attachment) => {
          setViewing(null);
          void save.run(async () => {
            await repository.remove(attachment.id);
            // 圖片刪除直接生效，不進垃圾桶，所以不提供復原。
            toast.show({ message: '已刪除圖片', detail: attachment.fileName });
          });
        }}
      />

      <Dialog
        open={pendingResize !== null}
        onOpenChange={(next) => {
          if (!next) setPendingResize(null);
        }}
        title="這張圖片很大，要縮小嗎？"
        description="縮小可以省下不少空間，備份檔也會小很多。原始比例會保留。"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const pending = pendingResize;
                setPendingResize(null);
                if (pending) void store(pending.file, false);
              }}
            >
              保留原尺寸
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const pending = pendingResize;
                setPendingResize(null);
                if (pending) void store(pending.file, true);
              }}
            >
              縮小到 2560px
            </Button>
          </>
        }
      >
        <p className="tabular text-[0.875rem] text-text-muted">
          目前是 {pendingResize?.width} × {pendingResize?.height}。
        </p>
      </Dialog>
    </section>
  );
}

function ThumbnailButton({ attachment, onOpen }: { attachment: Attachment; onOpen: () => void }) {
  const url = useBlobUrl(attachment.thumbnailBlob);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`開啟 ${attachment.fileName}`}
      className={cn(
        'size-20 overflow-hidden rounded-[var(--radius-control-lg)] border border-border',
        'transition-[transform,border-color] duration-200 ease-[var(--ease-gentle)]',
        'motion-safe:hover:-translate-y-0.5 hover:border-accent',
      )}
    >
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center bg-surface-sunken">
          <ImageSquare size={18} className="text-text-muted" aria-hidden="true" />
        </span>
      )}
    </button>
  );
}
